import React, { useState, useEffect, useRef } from "react";
import { FaUserCircle } from "react-icons/fa";
import { NavButton } from "./ModernButton.jsx";

function Header({ setOverrideContent, activeTab, setActiveTab, overrideContent }) {
  const [username, setUsername] = useState("");
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef();

  useEffect(() => {
    let storedUsername = localStorage.getItem("rememberUsername");

    if (storedUsername) {
      try {
        storedUsername = JSON.parse(storedUsername);
      } catch (e) {}
      setUsername(storedUsername);
    } else {
      setUsername("User");
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("rememberUsername");
    window.location.href = "/";
  };

  const handleProfile = () => {
    import("./Profile.jsx").then((module) => {
      setOverrideContent(<module.default />);
      setOpenMenu(false);
    });
  };

  return (
    <header className="bg-green-100 text-gray-900 shadow-lg border-b border-yellow-200 relative cursor-default select-none">
      <div className="w-full">
        <div className="flex items-center h-16 px-2">
          {/* Dead End Left: Logo + Title */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <div className="flex-shrink-0">
              <img
                src={require("../assets/cclogo.png")}
                alt="CC Logo"
                className="h-12 w-auto drop-shadow-sm"
              />
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl lg:text-2xl font-bold text-black leading-tight tracking-wide whitespace-nowrap">
                Perambalur Compost Shed Management
              </h1>
              <p className="text-sm text-gray-700 font-medium mt-0.5 tracking-wide">
                Smart Waste Management System
              </p>
            </div>
            {/* Mobile Title */}
            <div className="md:hidden">
              <h1 className="text-lg font-bold text-black tracking-wide whitespace-nowrap">Perambalur DRDA</h1>
              <p className="text-xs text-gray-700 tracking-wide">Waste Management</p>
            </div>
          </div>

          {/* Center: Navigation Tabs */}
          <div className="flex items-center space-x-2 flex-1 justify-center px-4">
            <NavButton
              isActive={activeTab === "Dashboard"}
              onClick={() => {
                setActiveTab("Dashboard");
                setOverrideContent(null);
              }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="#06B6D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                  <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                </svg>
              }
              hoverColor="#06B6D4"
              iconColor="#06B6D4"
            >
              Dashboard
            </NavButton>
            
            <NavButton
              isActive={activeTab === "Map View"}
              onClick={() => {
                setActiveTab("Map View");
                setOverrideContent(null);
              }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="#10B981" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
                  <path d="M15 5.764v15" />
                  <path d="M9 3.236v15" />
                </svg>
              }
              hoverColor="#10B981"
              iconColor="#10B981"
            >
              Map View
            </NavButton>
            
            <NavButton
              isActive={activeTab === "Insights"}
              onClick={() => {
                setActiveTab("Insights");
                setOverrideContent(null);
              }}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="#8B5CF6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              }
              hoverColor="#8B5CF6"
              iconColor="#8B5CF6"
            >
              Insights
            </NavButton>
          </div>

          {/* Dead End Right: Profile Menu */}
          <div className="flex items-center flex-shrink-0">
            <div className="relative" ref={menuRef}>
              <button
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/70 hover:bg-white border border-yellow-200 hover:border-yellow-300 transition-all duration-200 shadow-sm hover:shadow-md"
                onClick={() => setOpenMenu(!openMenu)}
              >
                <FaUserCircle className="text-lg text-gray-600" />
                <span className="hidden sm:block text-gray-700 font-medium">{username}</span>
                <svg 
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                    openMenu ? 'rotate-180' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {openMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] animate-in slide-in-from-top-2 duration-200 overflow-hidden">
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{username}</p>
                      <p className="text-xs text-gray-500">Waste Management User</p>
                    </div>
                    <button
                      onClick={handleProfile}
                      className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg 
                        className="w-4 h-4 text-gray-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile Settings
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      <svg 
                        className="w-4 h-4 text-red-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
